import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "url-search-params-polyfill"; // For IE11 & searchParams
// for Promises in IE11 I use the 'es6-promise.auto.min' library
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import ForumIcon from "@material-ui/icons/Forum";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import useValueRef from "./playerModules/hooks/useValueRef";
import {
  getUrlAllPrameters,
  generateUuid,
  setCookie,
  checkingTimerSleep,
  sendBeaconToAnalytics,
  sendPlayEvent,
  onWindowResize,
  getUuidCookeValue,
  onEventMessage,
  disposeVideojs,
} from "./playerModules/utils/utils";
import LiveChat from "./playerModules/LiveChat";

const IvsWithChat = props => {
  const eventUUID = props.uuid;
  const uuidCookieLifetime = props.uuidCookieLifetime;
  const analyticsDomain = props.domain + "/ex/live_analytics";

  const [playerOptions, setPlayerOptions] = useState({
    autoplay: true,
    fluid: false,
    liveui: false,
    controls: true,
    muted: true,
    controlBar: { fullscreenToggle: true, playToggle: false },
    html5: { hls: { withCredentials: false } },
    bigPlayButton: false,
  });
  const [liveEvent, setLiveEvent] = useState(props.live_event.data);
  const [isSearchParamsReady, setIsSearchParamsReady] = useState(false);
  const [paramUUID, setParamUUID] = useState("");
  const [clientUUID, setClientUUID] = useState("");
  const [player, setPlayer] = useState();
  const [playerType, setPlayerType] = useState(
    props.live_event.data.Player_type
  );
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [videoStatus, setVideoStatus] = useState({
    isPlaying: false,
    isFirstSendEvent: false,
  });
  const [sleepDuration, setSleepDuration] = useState(300);
  const [pollingDuration, setPollingDuration] = useState(60);
  const [isTracking, setIsTracking] = useState(true);
  const [chatEnable, setChatEnable] = useState(true);
  const [controller, setController] = useState(false);
  const [playerPaused, setPlayerPaused] = useState(false);

  const ws = useRef();
  const ctrTimerRef = useRef();

  // useValueRef : Ref is updated by useEffect for using in setInterval.
  const playerRef = useValueRef(player);
  const currentPlayerTypeRef = useValueRef(playerType);
  const isPlayingRef = useValueRef(videoStatus.isPlaying);
  const clientUUIDRef = useValueRef(clientUUID);

  const createWS = () => {
    ws.current = new WebSocket(props.ws);
    ws.current.onmessage = e => {
      const data = JSON.parse(e.data);
      if (data.evuuid !== eventUUID) {
        return;
      }
      changePlayerType(data.playerType);
      fetchLiveEvent();
    };

    ws.current.onerror = e => console.log("WebSocket error: " + e);
    ws.current.onopen = e => {
      const dataPlayer = { action: "subscribe", topic: "player" };
      ws.current.send(JSON.stringify(dataPlayer));
    };
    return () => ws.current.close();
  };

  const createServerSideEvents = () => {
    const evtSource = new EventSource(props.live_scheduler);

    evtSource.onopen = function () {
      console.log("Connection to server opened.");
    };

    evtSource.onmessage = function (e) {
      const data = JSON.parse(e.data);
      if (data.eventUUID === eventUUID && data.type === "update") {
        changePlayerType(data.playerType);
        fetchLiveEvent();
      }
    };

    evtSource.onerror = function (e) {
      console.log(e);
    };

    return () => evtSource.close();
  };

  const changePlayerType = type => {
    if (currentPlayerTypeRef.current !== type) {
      disposeVideojs("video-player");
      setPlayerType(type);
    }

    if (type === "poster") {
      setVideoStatus({
        ...videoStatus,
        isPlaying: false,
      });
    }
  };

  const fetchLiveEvent = () => {
    //fetch survapi
    axios({
      url: props.domain + "/ex/events/" + props.uuid + "?embed=channel",
      method: "GET",
      mode: "cors",
      headers: {
        Authorization: "Bearer " + props.token,
        "Content-Type": "application/json",
        Pragma: "no-cache",
      },
    })
      .then(function (res) {
        const data = res.data.data;
        setLiveEvent(data);
        changePlayerType(data.Player_type);
      })
      .catch(error => {
        console.log("Error: " + error);
      });
  };

  const checkUUID = () => {
    const { cookieValue, isPatternOk } = getUuidCookeValue(eventUUID);

    if (!isPatternOk) {
      // Create a new UUID
      // console.log("Generating a new UUID");
      const gClientUUID = generateUuid();
      setClientUUID(gClientUUID);
      // Saving the UUID in a cookie
      setCookie(eventUUID, clientUUID, uuidCookieLifetime);
    } else {
      // console.log("Using existing UUID");
      setClientUUID(cookieValue);
    }
  };

  const checkSerchParams = () => {
    //parameta
    const url = new URL(window.location);
    const searchParams = new URLSearchParams(url.search.slice(1));

    if (searchParams.has("autoplay")) {
      const valueString = searchParams.get("autoplay");
      const value = valueString === "true";

      setPlayerOptions({
        ...playerOptions,
        autoplay: value,
        muted: value,
      });
    }

    if (searchParams.has("uuid")) {
      const value = searchParams.get("uuid");
      if (value) {
        setParamUUID(value);
      }
    }

    if (searchParams.has("sleep")) {
      const value = searchParams.get("sleep");
      if (!isNaN(value)) {
        const numValue = Number(value);
        setSleepDuration(numValue);
      }
    }

    if (searchParams.has("polling")) {
      const value = searchParams.get("polling");
      if (!isNaN(value)) {
        const numValue = Number(value);
        setPollingDuration(numValue);
      }
    }

    if (searchParams.has("tracking")) {
      const valueString = searchParams.get("tracking");
      const bool = valueString !== "false";
      setIsTracking(bool);
    }

    setIsSearchParamsReady(true);
  };

  //player init
  const playerInit = options => {
    // Register Amazon IVS as playback technology for Video.js
    registerIVSTech(videojs);
    registerIVSQualityPlugin(videojs);

    const player = videojs(
      "video-player",
      {
        techOrder: ["AmazonIVS"],
        ...options,
      },
      () => {
        // Play stream
        player.enableIVSQualityPlugin();
        player.src([
          {
            type: "application/x-mpegURL",
            src: liveEvent.Channel.Custom_live_url,
          },
        ]);
        setPlayerPaused(playerRef.current.paused());
      }
    );

    player.on(["ended", "error", "pause"], e => {
      setVideoStatus({
        ...videoStatus,
        isPlaying: false,
      });
      setPlayerPaused(player.paused());
      //console.log("Playing : false " + e.type);
    });

    player.on(["playing", "play"], e => {
      setVideoStatus({
        ...videoStatus,
        isPlaying: true,
      });
      setPlayerPaused(player.paused());
      //console.log("Playing : true " + e.type);
    });

    //Sending the first beacon without waiting for n minutes
    player.one(["playing"], e => {
      setVideoStatus({
        ...videoStatus,
        isPlaying: true,
        isFirstSendEvent: true,
      });
    });

    player.on("ready", e => {
      window.parent.postMessage({ playerReady: true }, "*");
    });

    return player;
  };

  const onShowController = () => {
    setController(true);
    if (!playerPaused) {
      ctrTimerRef.current = setTimeout(() => {
        setController(false);
      }, 4000);
    }
  };
  const onHiddenController = e => {
    if (e.target.id === "controller") {
      ctrTimerRef.current && clearTimeout(ctrTimerRef.current);
      setController(false);
    }
  };
  const videoPlay = () => {
    playerRef.current.play();
    setPlayerPaused(false);
    ctrTimerRef.current = setTimeout(() => {
      setController(false);
    }, 4000);
  };
  const videoPause = () => {
    ctrTimerRef.current && clearTimeout(ctrTimerRef.current);
    playerRef.current.pause();
    setPlayerPaused(true);
  };

  /*
    DidMount
  */
  useEffect(() => {
    // Sending Beacon to Real Time Analytics every n minutes
    // Call the live analytics API every n minutes
    const interval = setInterval(() => {
      const enableTracking = isPlayingRef.current && isTracking;
      const uuid = paramUUID || clientUUIDRef.current;
      const analyticsUrl = analyticsDomain + "/" + eventUUID + "?uuid=" + uuid;
      sendBeaconToAnalytics(enableTracking, {
        url: analyticsUrl,
        token: props.token,
      });
    }, props.analytics_beacon_delay);

    // websocket
    createWS();

    // Server Side Events
    if (props.live_scheduler) {
      createServerSideEvents();
    }

    // Client UUID
    checkUUID();

    // SerchParams
    checkSerchParams();

    //postMessage
    window.addEventListener("message", e => {
      onEventMessage(e, playerRef.current);
    });

    onWindowResize();
    window.addEventListener("resize", onWindowResize);

    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", onWindowResize);
    };
  }, []);

  /*
    After GetSearchParams
  */
  useEffect(() => {
    let pollingInterval;

    if (isSearchParamsReady) {
      // If OS (PC) is sleepd for n seconds, Window is reloaded.
      const sleepTime = sleepDuration > 0 ? sleepDuration : 300;
      checkingTimerSleep(sleepTime);

      // Fetch EventAPI by polling every n minutes
      // Avoid risks for websocket failure
      const pollingTimeSeconds = pollingDuration > 0 ? pollingDuration : 60;
      const pollingTime = pollingTimeSeconds * 1000;
      pollingInterval = setInterval(() => {
        fetchLiveEvent();
      }, pollingTime);
    }

    return () => {
      clearInterval(pollingInterval);
    };
  }, [isSearchParamsReady]);

  /*
    player
  */
  useEffect(() => {
    if (
      liveEvent.Privacy ||
      playerType === "poster" ||
      !isSearchParamsReady ||
      isPlayerReady ||
      !liveEvent.Channel.Custom_live_url
    ) {
      return;
    }

    const scripts = [
      {
        name: "ivs-videojs-integraion",
        src: "https://player.live-video.net/1.7.0/amazon-ivs-videojs-tech.min.js",
        ref: "",
      },
      {
        name: "ivs-videojs-quality",
        src: "https://player.live-video.net/1.7.0/amazon-ivs-quality-plugin.min.js",
        ref: "",
      },
    ];

    // when all scripts are loaded, videojs play.
    let loadCounter = 0;
    const maxLoadedCount = scripts.length;

    scripts.forEach((scr, i) => {
      const script = document.createElement("script");
      script.src = scr.src;
      script.defer = true;
      document.body.appendChild(script);
      scripts[i].ref = script;

      script.addEventListener("load", () => {
        loadCounter++;
        if (loadCounter === maxLoadedCount) {
          const player = playerInit(playerOptions);
          setPlayer(player);
          setIsPlayerReady(true);
        }
      });
    });
    return () => {
      scripts.forEach(scr => {
        document.body.removeChild(scr.ref);
      });
      setIsPlayerReady(false);
    };
  }, [
    playerType,
    liveEvent.Privacy,
    isSearchParamsReady,
    liveEvent.Channel.Custom_live_url,
  ]);

  /*
    send Tracking API
  */
  useEffect(() => {
    if (!videoStatus.isFirstSendEvent) {
      return;
    }

    const enableTracking = isPlayingRef.current && isTracking;
    const uuid = paramUUID || clientUUIDRef.current;
    const analyticsUrl = analyticsDomain + "/" + eventUUID + "?uuid=" + uuid;
    const playEventUrl = props.domain + "/ex/play_event";

    sendBeaconToAnalytics(enableTracking, {
      url: analyticsUrl,
      token: props.token,
    });
    sendPlayEvent(isTracking, {
      url: playEventUrl,
      token: props.token,
      uuid,
      eventUuid: eventUUID,
    });
    setVideoStatus({
      ...videoStatus,
      isFirstSendEvent: false,
    });
  }, [videoStatus.isFirstSendEvent]);

  return !liveEvent.Privacy ? (
    <div className="wrapper with-chat">
      <div className="container">
        {playerType === "player" ? (
          <div className={`video-wrapper${controller ? " __controll" : ""}`}>
            <div id="video-inner" className="video-inner">
              <video
                id="video-player"
                className="video-js vjs-default-skin vjs-big-play-centered"
                playsInline
                preload="yes"
              ></video>
            </div>
            <div
              key="player"
              className="video-layer"
              onClick={onShowController}
            ></div>
          </div>
        ) : (
          <div
            className="video-poster"
            style={{ backgroundImage: `url(${liveEvent.Poster_url})` }}
          >
            <div
              key="poster"
              className="video-layer"
              onClick={onShowController}
            ></div>
          </div>
        )}
        <div className={`chat-wrapper${chatEnable ? " __show" : ""}`}>
          <LiveChat
            src={`https://${props.livechat_domain}?${getUrlAllPrameters(
              window.location.href
            ).join("&")}`}
          />
        </div>
        <div
          id="controller"
          className={`controller${controller ? " __show" : ""}`}
          onClick={onHiddenController}
        >
          <div className="controller__head">
            <button
              className="controller__head-button chat-button"
              onClick={() => {
                setChatEnable(pre => !pre);
              }}
            >
              {chatEnable ? <ForumIcon /> : <ForumOutlinedIcon />}
            </button>
          </div>
          {playerType === "player" && (
            <React.Fragment>
              <button
                className={`controller__play-button play-button${
                  playerPaused ? " __show" : ""
                }`}
                onClick={videoPlay}
              >
                <PlayArrowIcon />
              </button>
              <button
                className={`controller__play-button play-button${
                  !playerPaused ? " __show" : ""
                }`}
                onClick={videoPause}
              >
                <PauseIcon />
              </button>
            </React.Fragment>
          )}
        </div>
      </div>
    </div>
  ) : (
    <h1 className="title permission-message">
      Sorry, this video is private.
      <br />
      この動画は非公開です。
    </h1>
  );
};

export default IvsWithChat;
